import { COVERAGE_TYPE_CODESYSTEM, INSURANCE_COVERAGE_TYPE_CODE, Track } from '@bariendo/lib/constants';
import {
  ELIGIBILITY_QUESTIONNAIRE_URL,
  EligibilityIntakeSelections,
  readEligibilityIntakeSelections,
} from '@bariendo/lib/eligibility-intake';
import { PatientAccessCallAnswers } from '@bariendo/lib/patient-access-note';
import { getReferenceString, MedplumClient } from '@medplum/core';
import { Organization, Patient, Reference } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useEffect, useState } from 'react';
import { usePatientTrack } from '@/hooks/usePatientTrack';
import {
  CONSULTATION_NOTE_CATEGORY_TOKEN,
  readSavedCallNote,
} from '@/pages/PatientTimelinePage/PatientSummary/IntakeBilling/services/introCallNote';
import { canSearchResourceType } from '@/services/permissions';
import { likelyLocation, likelyProcedure, likelyTrack } from './preConsultationFacts';

export interface PreConsultationSources {
  /** The newest saved consultation note's answers — never a draft, never this card's live answers. */
  savedAnswers?: PatientAccessCallAnswers;
  eligibility: EligibilityIntakeSelections;
}

/**
 * The two reads the Intake and billing panel makes for Service and Location: the
 * eligibility submission and the saved consultation notes. Same filters and the
 * same reasons — see `useIntakeBilling`. A failed read leaves its fact to the
 * other sources rather than breaking the card.
 */
export async function readPreConsultationSources(
  medplum: MedplumClient,
  patient: Patient
): Promise<PreConsultationSources> {
  const subject = getReferenceString(patient);

  const [eligibility, notes] = await Promise.all([
    medplum
      .searchResources('QuestionnaireResponse', {
        subject,
        questionnaire: ELIGIBILITY_QUESTIONNAIRE_URL,
        _count: 1,
        _sort: '-authored',
      })
      .catch(() => []),
    medplum
      .searchResources('Communication', {
        subject,
        category: CONSULTATION_NOTE_CATEGORY_TOKEN,
        status: 'completed',
        _count: 5,
        _sort: '-sent',
      })
      .catch(() => []),
  ]);

  const savedAnswers = readSavedCallNote(notes)?.answers;

  return {
    ...(savedAnswers ? { savedAnswers } : {}),
    eligibility: readEligibilityIntakeSelections(eligibility[0]),
  };
}

/**
 * The insurer: the payor Organization's name on the patient's newest active
 * insurance Coverage. Undefined while the patient is still the payor (signup has
 * not resolved the insurer, BAR-2905), and for a viewer whose policy has no
 * Coverage: a search naming a type outside the policy is refused, not filtered.
 */
export async function readInsurerName(medplum: MedplumClient, patient: Patient): Promise<string | undefined> {
  if (!canSearchResourceType(medplum, 'Coverage')) return undefined;

  try {
    const coverage = await medplum.searchOne('Coverage', {
      patient: getReferenceString(patient),
      type: `${COVERAGE_TYPE_CODESYSTEM}|${INSURANCE_COVERAGE_TYPE_CODE}`,
      status: 'active',
      _sort: '-_lastUpdated',
    });
    const payor = coverage?.payor?.find((reference) => reference.reference?.startsWith('Organization/'));
    if (!payor) return undefined;

    const organization = await medplum.readReference(payor as Reference<Organization>);
    return organization.name;
  } catch {
    return undefined;
  }
}

export interface PreConsultationFacts {
  procedure?: string;
  location?: string;
  /** Undefined while the track (and, on the insurance track, the insurer) loads, so it never flashes a partial value. */
  track?: string;
  loading: boolean;
}

/**
 * The PN card's pinned facts block: likely procedure, location and track.
 *
 * `enabled` is false for a saved note, which does not show the facts; nothing
 * is read then.
 */
export function usePreConsultationFacts(patient: Patient | undefined, enabled: boolean): PreConsultationFacts {
  const medplum = useMedplum();
  const patientToRead = enabled ? patient : undefined;
  const { track, loading: trackLoading } = usePatientTrack(patientToRead?.id);
  const [sources, setSources] = useState<PreConsultationSources>();
  // Wrapped so "read, no insurer named" is distinguishable from "not read yet".
  const [insurer, setInsurer] = useState<{ name?: string }>();

  useEffect(() => {
    if (!patientToRead?.id) return;
    let cancelled = false;
    readPreConsultationSources(medplum, patientToRead)
      .then((loaded) => {
        if (!cancelled) setSources(loaded);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [medplum, patientToRead]);

  // Only the insurance track names an insurer, so only it pays for the read.
  useEffect(() => {
    if (!patientToRead?.id || track !== Track.INSURANCE) return;
    let cancelled = false;
    setInsurer(undefined);
    readInsurerName(medplum, patientToRead)
      .then((name) => {
        if (!cancelled) setInsurer({ name });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [medplum, patientToRead, track]);

  const eligibility = sources?.eligibility ?? {};
  const trackPending = trackLoading || (Boolean(patientToRead?.id) && track === Track.INSURANCE && !insurer);

  return {
    procedure: likelyProcedure(patient, sources?.savedAnswers, eligibility),
    location: likelyLocation(patient, sources?.savedAnswers, eligibility),
    track: trackPending ? undefined : likelyTrack(track, patient, insurer?.name),
    loading: (Boolean(patientToRead?.id) && !sources) || trackPending,
  };
}
